import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

export const fetchPnlHistory = createAsyncThunk(
  'pnlHistory/fetchPnlHistory',
  async (params = {}) => {
    const response = await api.get('/api/pnl-history/', { params });
    return response.data;
  }
);

export const fetchPnlSummary = createAsyncThunk(
  'pnlHistory/fetchSummary',
  async (params = {}) => {
    const response = await api.get('/api/pnl-history/summary/', { params });
    return response.data;
  }
);

export const fetchRelatedTrades = createAsyncThunk(
  'pnlHistory/fetchRelatedTrades',
  async (pnlUuid) => {
    const response = await api.get(`/api/pnl-history/${pnlUuid}/related_trades/`);
    return response.data;
  }
);

export const deletePnlHistory = createAsyncThunk(
  'pnlHistory/deletePnlHistory',
  async ({ pnlUuid, deleteTrades = true }, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/api/pnl-history/${pnlUuid}/delete_with_trades/`, {
        params: { delete_trades: deleteTrades }
      });
      return { ...response.data, deletedUuid: pnlUuid };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to delete' });
    }
  }
);

export const fetchPnlDailyChart = createAsyncThunk(
  'pnlHistory/fetchDailyChart',
  async (params = {}) => {
    const response = await api.get('/api/pnl-history/daily_chart/', { params });
    return response.data;
  }
);

export const fetchPnlExchangePairs = createAsyncThunk(
  'pnlHistory/fetchExchangePairs',
  async () => {
    const response = await api.get('/api/pnl-history/exchange_pairs/');
    return response.data;
  }
);

const pnlHistorySlice = createSlice({
  name: 'pnlHistory',
  initialState: {
    pnlHistory: [],
    count: 0, // Total count for pagination
    summary: {
      total_pnl: 0,
      total_fee: 0,
      trade_count: 0,
      avg_premium: 0,
    },
    exchangePairs: [], // Available exchange pairs for filtering
    loading: false,
    error: null,
    filters: {
      start_date: null,
      end_date: null,
      trading_pair: null,
    },
    // Delete-related state
    deleting: false,
    deleteError: null,
    relatedTrades: null,
    loadingRelatedTrades: false,
    // Chart data state
    chartData: [],
    chartLoading: false,
    chartTotalPnl: 0,
  },
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearDeleteError: (state) => {
      state.deleteError = null;
    },
    clearRelatedTrades: (state) => {
      state.relatedTrades = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPnlHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPnlHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.pnlHistory = action.payload.results || action.payload;
        state.count = action.payload.count || (action.payload.results ? action.payload.results.length : action.payload.length);
      })
      .addCase(fetchPnlHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchPnlSummary.fulfilled, (state, action) => {
        state.summary = action.payload;
      })
      // Delete PnL history
      .addCase(deletePnlHistory.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
      })
      .addCase(deletePnlHistory.fulfilled, (state, action) => {
        state.deleting = false;
        // Remove the deleted item from the list
        state.pnlHistory = state.pnlHistory.filter(
          pnl => pnl.uuid !== action.payload.deletedUuid
        );
        state.count = Math.max(0, state.count - 1);
      })
      .addCase(deletePnlHistory.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload?.error || 'Failed to delete PnL history';
      })
      // Fetch related trades
      .addCase(fetchRelatedTrades.pending, (state) => {
        state.loadingRelatedTrades = true;
      })
      .addCase(fetchRelatedTrades.fulfilled, (state, action) => {
        state.loadingRelatedTrades = false;
        state.relatedTrades = action.payload;
      })
      .addCase(fetchRelatedTrades.rejected, (state) => {
        state.loadingRelatedTrades = false;
        state.relatedTrades = null;
      })
      // Fetch daily chart data
      .addCase(fetchPnlDailyChart.pending, (state) => {
        state.chartLoading = true;
      })
      .addCase(fetchPnlDailyChart.fulfilled, (state, action) => {
        state.chartLoading = false;
        state.chartData = action.payload.data || [];
        state.chartTotalPnl = action.payload.total_pnl || 0;
      })
      .addCase(fetchPnlDailyChart.rejected, (state) => {
        state.chartLoading = false;
        state.chartData = [];
      })
      .addCase(fetchPnlExchangePairs.fulfilled, (state, action) => {
        state.exchangePairs = action.payload;
      });
  },
});

export const { setFilters, clearDeleteError, clearRelatedTrades } = pnlHistorySlice.actions;
export default pnlHistorySlice.reducer;